import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProjects } from "../../api";
import { Project } from "../../types";

const Projects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProjects()
      .then((res) => {
        setProjects(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch projects:", err);
        setLoading(false);
      });
  }, []);

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500 text-white";
      case "ongoing":
        return "bg-yellow-500 text-white";
      case "planning":
        return "bg-blue-500 text-white";
      default:
        return "bg-red-500 text-white";
    }
  };

  return (
    <>
      <div className="border-b border-gray-100 bg-white pb-16 pt-[120px] text-gray-900 md:pb-20 md:pt-[140px]">
        <div className="mx-auto max-w-[1400px] px-6 text-center lg:px-8">
          <div className="mb-6">
            <i className="fas fa-hard-hat text-5xl text-indigo-600 md:text-6xl"></i>
          </div>
          <h1 className="mb-4 text-4xl font-bold md:text-5xl lg:text-6xl">
            Community Building Projects
          </h1>
          <p className="text-lg text-gray-600 md:text-xl">
            Track our village development and infrastructure initiatives
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] bg-white px-6 py-16 md:py-20 lg:px-8 lg:py-24">
        {loading ? (
          <p>Loading projects...</p>
        ) : projects.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
            {projects.map((project) => (
              <div
                key={project.id}
                className="transform overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  {project.featured_image ? (
                    <img
                      src={`/storage/${project.featured_image}`}
                      alt={project.title}
                      className="h-56 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-56 w-full items-center justify-center bg-gray-100">
                      <i className="fas fa-building text-5xl text-gray-400"></i>
                    </div>
                  )}
                  <div className="absolute right-4 top-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(
                        project.status
                      )}`}
                    >
                      {project.status}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-3 flex items-center justify-between">
                    {project.images_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                        <i className="fas fa-images text-indigo-600"></i>{" "}
                        {project.images_count} photos
                      </span>
                    )}
                    {project.budget && (
                      <span className="font-semibold text-indigo-600">
                        ₦{Number(project.budget).toLocaleString()}
                      </span>
                    )}
                  </div>

                  <h3 className="mb-2 text-xl font-bold text-gray-900">
                    {project.title}
                  </h3>

                  {project.location && (
                    <p className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600">
                      <i className="fas fa-map-marker-alt text-red-500"></i>{" "}
                      {project.location}
                    </p>
                  )}

                  <p className="mb-4 line-clamp-3 text-gray-600">
                    {project.description.substring(0, 120)}...
                  </p>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                    <div className="text-sm text-gray-500">
                      {project.start_date && (
                        <>
                          <i className="fas fa-calendar-alt text-indigo-600"></i>{" "}
                          {new Date(project.start_date).toLocaleDateString(
                            "en-US",
                            { month: "short", year: "numeric" }
                          )}
                        </>
                      )}
                    </div>
                    <Link
                      to={`/projects/${project.id}`}
                      className="group/link inline-flex items-center gap-2 font-bold text-indigo-600 hover:text-indigo-800"
                    >
                      View Details
                      <i className="fas fa-arrow-right transition-transform group-hover/link:translate-x-1"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
              <i className="fas fa-hard-hat text-5xl text-gray-400"></i>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-800">
              No Projects Found
            </h2>
            <p className="text-gray-600">
              There are currently no projects to display. Please check back
              later.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default Projects;
